import React, { useEffect } from 'react';
import styled from 'styled-components';

import { IClientContact, ISupplierContact } from '@bb/contacts';

import {
  BuyerAppointmentListItem,
  Divider,
  ListingListItem,
  Tabs,
  TabPane,
  theme,
} from 'components';
import type { GetSimpleListingsByIdsDTO } from '@/app-package';
import { PopulatedUser } from '../../../api/UserController/getProfile';

import { ContactDrawerType, ContactDrawerUser } from '../types';

import ContactDetails from './Details/ContactDetails';
import UserDetails from './Details/UserDetails';
import DetailsSkeleton from './Details/Skeleton';
import {
  Container,
  ContentContainer,
  HeaderContainer,
  HeaderText,
  ListingContainer,
  StyledContent,
} from './Details/common';

interface DrawerDetailsProps {
  contact: ContactDrawerUser & { showings?: any[] };
  linkedContacts: IClientContact[];
  populatedListings?: GetSimpleListingsByIdsDTO['listings'];
  loading: boolean;
  canResendClientInvitation: boolean;
  goToContact: (type: ContactDrawerType, contactId: string) => void;
  goToListing: (listingId: string) => void;
  resendClientInvitation: (clientId: string) => Promise<void>;
  type: ContactDrawerType;
}

const DrawerDetails: React.FC<DrawerDetailsProps> = ({
  contact,
  type,
  loading,
  populatedListings,
  goToListing,
  ...props
}) => {
  const [activeKey, setActiveKey] = React.useState('');

  useEffect(() => {
    if (contact?.showings?.length) setActiveKey('showings');
    else setActiveKey('listings');
  }, [populatedListings?.length, contact?.showings?.length]);

  const showingsAndListingsTabs =
    populatedListings?.length || contact?.showings?.length ? (
      <StyledTabs activeKey={activeKey} onChange={setActiveKey}>
        {contact?.showings?.length ? (
          <TabPane key="showings" tab="Buyer Showings">
            <StyledContent data-cy="contact-details-showings-container">
              {contact.showings.map(appointment => (
                <BuyerAppointmentListItem
                  key={appointment.id}
                  appointment={appointment}
                />
              ))}
            </StyledContent>
          </TabPane>
        ) : null}
        {populatedListings?.length ? (
          <TabPane key="listings" tab="Listings">
            <StyledContent data-cy="contact-details-listing-container">
              {populatedListings.map(listing => (
                <TabbedListingContainer
                  key={listing.id}
                  onClick={() => goToListing(listing.id)}
                >
                  {/* @ts-expect-error Need to update SimpleListing in domain to include cityand province */}
                  <ListingListItem listing={listing} />
                </TabbedListingContainer>
              ))}
            </StyledContent>
          </TabPane>
        ) : null}
      </StyledTabs>
    ) : null;

  if (loading || !contact) {
    return DetailsSkeleton;
  }

  return (
    <Container>
      <HeaderContainer>
        <HeaderText>Details</HeaderText>
      </HeaderContainer>
      <Divider />

      <StyledContent>
        <ContentContainer>
          {type === 'contact' && (
            <ContactDetails
              contact={contact as IClientContact | ISupplierContact}
              {...props}
            />
          )}
          {type === 'user' && (
            <UserDetails contact={contact as PopulatedUser} {...props} />
          )}
        </ContentContainer>
      </StyledContent>
      {showingsAndListingsTabs}
    </Container>
  );
};

export default DrawerDetails;

const ListingsContainer = styled.div`
  padding-top: 20px;
`;

const StyledTabs = styled(Tabs)`
  padding: 16px;
  .ant-tabs-tab-active > .ant-tabs-tab-btn {
    color: ${theme.blue6};
  }
`;

const TabbedListingContainer = styled.div`
  cursor: pointer;
`;
